import { EDocMetadataProps } from '@/components/EDocMetadata';
import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import {
  ActiveFileType,
  usePaymentDetail,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import {
  ElectronicTag,
  TimestampTag,
  type Status,
} from '@/features/InvoiceReport/Components/Tags';
import { useReceiptTypeChecks } from '@/hooks/useReceiptTypeChecks';
import {
  IconButton,
  IconNewWindow,
} from '@moneyforward/ap-frontend-components';
import type { InvoiceReportReceiptType } from 'ap-openapi';
import classNames from 'classnames';
import { FC, memo, useMemo } from 'react';
import { Details } from './Details';
import styles from './Header.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  title?: string;
  timestamp?: boolean;
  scanDecision?: Status;
  isEmpty?: boolean;
  eDocMeta?: EDocMetadataProps;
  zoom?: boolean;
  pdfUrl?: string;
  onPictureInPicture?: () => void;
  isPiPSupport?: boolean;
  receiptType?: InvoiceReportReceiptType;
  onOpenFileInfoDrawer?: () => void;
};

export const Header: FC<Props> = memo(
  ({
    title,
    timestamp,
    scanDecision,
    isEmpty,
    eDocMeta,
    zoom,
    onPictureInPicture,
    isPiPSupport,
    receiptType,
    onOpenFileInfoDrawer,
  }) => {
    const { isPaperReceipt } = useReceiptTypeChecks(receiptType);
    const { activeFile } = usePaymentDetail();
    const isShowElectronicTag = useMemo(
      () => Boolean(eDocMeta && scanDecision && isPaperReceipt),
      [eDocMeta, scanDecision, isPaperReceipt]
    );

    const isBusinessDocument =
      activeFile?.type === ActiveFileType.BUSINESS_DOCUMENT;

    return (
      <div
        className={cx(styles['container'], {
          [styles['non-attachment']]: isEmpty,
        })}
      >
        {!isEmpty && title && (
          <FileNameSeparatorWithExtension
            containerClassName={cx(styles['title'])}
          >
            {title}
          </FileNameSeparatorWithExtension>
        )}
        {!zoom && (
          <div className={cx(styles['e-doc-container'])}>
            {!isEmpty && timestamp && <TimestampTag />}
            {!isEmpty && isShowElectronicTag && (
              <ElectronicTag status={scanDecision!} />
            )}
            <Details
              eDocMeta={eDocMeta}
              onOpenFileInfoDrawer={onOpenFileInfoDrawer}
              isEmpty={isEmpty}
              isBusinessDocument={isBusinessDocument}
            />
          </div>
        )}
        {isPiPSupport && !isBusinessDocument && (
          <IconButton
            onClick={onPictureInPicture}
            size='xs'
            className={cx(styles['black-button'])}
          >
            <IconNewWindow size={14} />
          </IconButton>
        )}
      </div>
    );
  }
);
Header.displayName = 'Header';
